import { FC, forwardRef, MouseEvent as ReactMouseEvent } from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FAIcon, Tooltip, TooltipPreference, filterIconTheme, IconStyle } from '../../../';
import { InputFieldPropsTypes } from '../types/propsType';
import { InputIconPosition, InputSize, InputTypes, InputValidations, LabelPosition } from '../types/enums';
import styles from './ClassicInput.module.scss';

/**
 * @description Input provide <input></input> with basic css;
 *  styling can be override through prop (className) only.
 *  by default : disabled, readOnly are false;
 */

const ClassicInput: FC<InputFieldPropsTypes> = forwardRef<HTMLInputElement, InputFieldPropsTypes>(
  ({
    id = '',
    name = '',
    label = '',
    message = '',
    className = '',
    labelInfo = '',
    labelInfoPreference = TooltipPreference.TOP_CENTER,
    labelClassName = '',
    disabled = false,
    readOnly = false,
    status,
    onClear,
    onToggleVisibility,
    inputIcon,
    inputIconPosition = InputIconPosition.DEFAULT,
    forwardedRef,
    required = false,
    dashboard = false,
    inputIconOnClick,
    inputSize = InputSize.MEDIUM,
    labelPosition = LabelPosition.DEFAULT,
    ...restProps
  }) => {
    const inputType = restProps.type === InputTypes.NUMBER ? InputTypes.TEXT : restProps.type;

    const { DEFAULT, VALID, INVALID, WARNING } = InputValidations;
    const currentIcon = inputIcon
      ? inputIcon
      : status === VALID
        ? { iconName: 'circle-check' as IconName }
        : status === INVALID || status === WARNING
          ? { iconName: 'circle-exclamation' as IconName }
          : onClear
            ? { iconName: 'x-mark' as IconName }
            : null;

    const onClick = (e: ReactMouseEvent<HTMLSpanElement, MouseEvent>) => {
      if (onClear) {
        onClear(e);
      } else if (onToggleVisibility) {
        onToggleVisibility(e);
      }
    };

    return (
      <div
        id={id}
        className={`${labelPosition === LabelPosition.LEFT ? styles['input-left'] : styles['input']} ${
          status && status !== DEFAULT
            ? status === VALID
              ? styles['input-validated-valid']
              : status === INVALID
                ? styles['input-validated-invalid']
                : styles['input-validated-warning']
            : ''
        } ${inputIcon && !status ? styles['input-icon-color'] : ''} ${styles['input-default-behaviour']} ${styles[inputSize]}`}
      >
        {label && (
          <label htmlFor={name} className={styles['input-label']}>
            <span
              className={`${styles['input-label-text']} ${labelClassName !== '' ? labelClassName : ''}`}
              data-testid="input-label-text-test"
            >
              {label} {required && <span className={styles['input-label-red']}>*</span>}
            </span>
            {labelInfo && (
              <Tooltip message={labelInfo} preference={labelInfoPreference} className={styles['input-tool-tip']} />
            )}
          </label>
        )}
        <div className={styles['input-field-container-outer']}>
          <div
            className={
              `${styles['input-field-container']} ${disabled ? styles['disabled'] : ''} ${dashboard ? styles['dashboard'] : ''}` +
              ` ${readOnly ? styles['read-only'] : ''}`
            }
          >
            {currentIcon && !inputIconPosition && (
              <FAIcon
                theme={filterIconTheme(restProps.theme)}
                data-testid="input-icon-test-id"
                className={`${styles['input-icon']} ${styles['input-box-icon']} ${onToggleVisibility && styles['icon-toggle-visibility']}`}
                onClick={(e: ReactMouseEvent<HTMLSpanElement, MouseEvent>) =>
                  inputIconOnClick ? inputIconOnClick?.(e) : onClick
                }
                iconStyle={IconStyle.SOLID}
                {...currentIcon}
              />
            )}
            <input
              className={`${styles['input-field']} ${className}`}
              data-testid="input-test-id"
              ref={forwardedRef}
              id={name}
              name={name}
              disabled={disabled}
              readOnly={readOnly}
              {...restProps}
              type={inputType}
            />

            {currentIcon && inputIconPosition === InputIconPosition.RIGHT && (
              <>
              <FAIcon
                theme={filterIconTheme(restProps.theme)}
                data-testid="input-icon-test-id"
                className={`${styles['input-icon']} ${styles['input-box-icon']} ${onToggleVisibility && styles['icon-toggle-visibility']}`}
                onClick={(e: ReactMouseEvent<HTMLSpanElement, MouseEvent>) =>
                  inputIconOnClick ? inputIconOnClick?.(e) : onClick
                }
                iconStyle={IconStyle.SOLID}
                {...currentIcon}
              /></>
             
            )}
          </div>
          {message && (
            <span
              role="presentation"
              aria-label="message"
              aria-details={`${label} input message`}
              className={inputIcon ? `${styles['input-icon-message']}`: `${styles['input-message']}`}
            >
              {message}
            </span>
          )}
        </div>
      </div>
    );
  },
);
ClassicInput.displayName = 'ClassicInput';
export default ClassicInput;
